import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { STORAGE_URL } from '../../services/api';
import { 
  Save, Search, CheckCircle, 
  Trash2, Edit, Eye, MessageSquare, X, Star
} from 'lucide-react';
// 👇 1. Common Layout aur PageTitle import kiya gaya hai (Sidebar hata diya hai)
import AdminLayout from '../layouts/AdminLayout';
import PageTitle from '../components/ui/PageTitle';

const AdminTestimonials = () => {
  // Form States
  const [name, setName] = useState('');
  const [designation, setDesignation] = useState('');
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(5);
  const [imageFile, setImageFile] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Table States
  const [testimonials, setTestimonials] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [viewData, setViewData] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  
  const [editData, setEditData] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const adminToken = localStorage.getItem('admin_token');

  const fetchTestimonials = async () => {
    setTableLoading(true);
    try {
      const response = await api.get("/testimonials");
      setTestimonials(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => { fetchTestimonials(); }, []);

  // Add Testimonial
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const formData = new FormData();
    formData.append('name', name);
    formData.append('designation', designation);
    formData.append('message', message);
    formData.append('rating', rating);
    formData.append('status', 1); // Default active
    if (imageFile) formData.append('image', imageFile);

   try {
      await api.post('/testimonials', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data', 
          'Authorization': `Bearer ${adminToken}` 
        }
      });
      setSuccess(true);
      setName(''); setDesignation(''); setMessage(''); setRating(5); setImageFile(null);
      document.getElementById('add-form').reset();
      fetchTestimonials();
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error(error);
      alert("Failed to add testimonial.");
    } finally {
      setLoading(false);
    }
  };

  // Status Toggle
  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = (currentStatus == 1 || currentStatus === 'active') ? 0 : 1;
    setTestimonials(testimonials.map(t => t.id === id ? { ...t, status: newStatus } : t));
    try {
      await api.post(`/testimonials-status/${id}`, 
      { status: newStatus }, 
      { headers: { 'Authorization': `Bearer ${adminToken}` } });
    } catch (error) {
      console.error(error);
      alert("Failed to update status");
      fetchTestimonials(); 
    }
  };

  // View & Edit logic
  const handleView = (t) => { setViewData(t); setIsViewOpen(true); };
  const openEdit = (t) => { setEditData({ ...t, imageFile: null }); setIsEditOpen(true); };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    const formData = new FormData();
    formData.append('name', editData.name);
    formData.append('designation', editData.designation);
    formData.append('message', editData.message);
    formData.append('rating', editData.rating);
    if (editData.imageFile) formData.append('image', editData.imageFile);

    try {
      await api.post(`/testimonials-update/${editData.id}`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data', 
          'Authorization': `Bearer ${adminToken}` 
        }
      });
      fetchTestimonials();
      setIsEditOpen(false);
    } catch (error) {
      console.error(error);
      alert("Failed to update.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this testimonial?")) {
      try {
        await api.delete(`/testimonials/${id}`, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        setTestimonials(testimonials.filter(t => t.id !== id));
      } catch (error) {
        console.error(error);
        alert("Failed to delete.");
      }
    }
  };

  const filteredData = testimonials.filter(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()));

  // 👇 2. Ab poora page <AdminLayout> se wrap hai
  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto space-y-8">
        
        {/* 👇 3. Common PageTitle component use ho raha hai */}
        <div className="mb-2">
          <PageTitle 
            title="Manage Testimonials" 
            subtitle="Add and manage client or associate testimonials for the homepage." 
          />
        </div>

        {/* ADD FORM */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <h2 className="text-lg font-bold text-gray-800 mb-6 border-b pb-3 border-gray-100 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#FED547]" /> Add New Testimonial
          </h2>
          {success && (
            <div className="bg-green-50 text-green-700 p-4 rounded-xl mb-6 flex items-center text-sm font-medium border border-green-100">
              <CheckCircle className="w-5 h-5 mr-2 text-green-500" /> Testimonial added successfully!
            </div>
          )}
          
          <form id="add-form" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Associate Name *</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#FED547] focus:ring-1 outline-none" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Designation / Role</label>
                <input type="text" placeholder="e.g. Kadtech Associate" value={designation} onChange={(e) => setDesignation(e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#FED547] focus:ring-1 outline-none" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                <textarea rows="3" value={message} onChange={(e) => setMessage(e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#FED547] focus:ring-1 outline-none" required></textarea>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rating (Stars)</label>
                <select value={rating} onChange={(e) => setRating(e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-[#FED547] focus:ring-1 outline-none">
                  <option value="5">5 Stars - Excellent</option>
                  <option value="4">4 Stars - Good</option>
                  <option value="3">3 Stars - Average</option>
                  <option value="2">2 Stars - Poor</option>
                  <option value="1">1 Star - Terrible</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Avatar / Photo</label>
                <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} className="w-full border border-gray-200 rounded-xl px-2 py-1.5 text-sm file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:bg-yellow-50 file:text-yellow-700 cursor-pointer bg-white" />
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button type="submit" disabled={loading} className="bg-[#FED547] text-black px-6 py-2.5 rounded-lg font-bold flex items-center gap-2 hover:bg-[#e5be3c]">
                <Save className="w-4 h-4" /> {loading ? 'Saving...' : 'Save Testimonial'}
              </button>
            </div>
          </form>
        </div>

        {/* DATA TABLE */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex justify-between gap-4 bg-gray-50/50">
            <h2 className="text-lg font-bold text-gray-800">Added Testimonials</h2>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Search name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="border border-gray-200 rounded-full pl-9 pr-4 py-2 text-sm w-64 outline-none focus:border-[#FED547]" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600">
              <thead className="bg-[#FED547] text-black font-bold">
                <tr>
                  <th className="py-4 px-6 text-xs w-16">S.NO</th>
                  <th className="py-4 px-6 text-xs">AVATAR</th>
                  <th className="py-4 px-6 text-xs">DETAILS</th>
                  <th className="py-4 px-6 text-xs">STATUS</th>
                  <th className="py-4 px-6 text-xs text-center">ACTIONS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tableLoading ? <tr><td colSpan="5" className="text-center py-10">Loading...</td></tr> : 
                 filteredData.length === 0 ? <tr><td colSpan="5" className="text-center py-10 text-gray-400">No testimonials found.</td></tr> :
                 filteredData.map((t, index) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="py-3 px-6">{index + 1}</td>
                    <td className="py-3 px-6">
                      {t.image ? (
                        <img src={STORAGE_URL(t.image)} alt={t.name} className="w-10 h-10 rounded-full object-cover border border-gray-200" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-500">{t.name ? t.name.charAt(0) : 'T'}</div>
                      )}
                    </td>
                    <td className="py-3 px-6">
                      <div className="font-bold text-gray-800">{t.name}</div>
                      <div className="text-xs text-gray-500">{t.designation}</div>
                      <div className="flex gap-0.5 mt-1">
                        {[...Array(Number(t.rating) || 5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />)}
                      </div>
                    </td>
                    
                    {/* STATUS TOGGLE */}
                    <td className="py-3 px-6">
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleToggleStatus(t.id, t.status)} className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${t.status == 1 ? 'bg-green-500' : 'bg-gray-300'}`}>
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${t.status == 1 ? 'translate-x-5' : 'translate-x-1'}`} />
                        </button>
                        <span className={`text-xs font-bold ${t.status == 1 ? 'text-green-600' : 'text-gray-400'}`}>{t.status == 1 ? 'Active' : 'Inactive'}</span>
                      </div>
                    </td>

                    <td className="py-3 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleView(t)} className="p-1.5 text-gray-400 hover:text-blue-500"><Eye className="w-4 h-4" /></button>
                        <button onClick={() => openEdit(t)} className="p-1.5 text-gray-400 hover:text-yellow-500"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(t.id)} className="p-1.5 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* VIEW MODAL (Fixed property applied) */}
      {isViewOpen && viewData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md relative shadow-xl">
            <button onClick={() => setIsViewOpen(false)} className="absolute top-4 right-4 text-gray-500 hover:text-black"><X className="w-5 h-5"/></button>
            <div className="flex flex-col items-center text-center mt-4">
              {viewData.image ? (
                <img src={STORAGE_URL(viewData.image)} className="w-20 h-20 rounded-full object-cover border-4 border-yellow-100 mb-3" alt="avatar"/>
              ) : (
                <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-2xl font-bold mb-3">{viewData.name.charAt(0)}</div>
              )}
              <h4 className="text-xl font-bold">{viewData.name}</h4>
              <p className="text-sm text-gray-500 mb-2">{viewData.designation}</p>
              <div className="flex gap-1 mb-4">
                {[...Array(Number(viewData.rating) || 5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
              </div>
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 text-sm text-gray-700 italic">
                "{viewData.message}"
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL (Fixed property applied) */}
      {isEditOpen && editData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg relative max-h-[90vh] overflow-y-auto shadow-xl">
            <button onClick={() => setIsEditOpen(false)} className="absolute top-4 right-4 text-gray-500 hover:text-black"><X className="w-5 h-5"/></button>
            <h3 className="text-lg font-bold mb-4">Edit Testimonial</h3>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm mb-1 block">Name</label>
                  <input type="text" value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:border-yellow-400" required />
                </div>
                <div>
                  <label className="text-sm mb-1 block">Designation</label>
                  <input type="text" value={editData.designation} onChange={(e) => setEditData({...editData, designation: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:border-yellow-400" />
                </div>
              </div>
              <div>
                <label className="text-sm mb-1 block">Message</label>
                <textarea rows="3" value={editData.message} onChange={(e) => setEditData({...editData, message: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:border-yellow-400" required></textarea>
              </div>
              <div>
                <label className="text-sm mb-1 block">Rating</label>
                <select value={editData.rating} onChange={(e) => setEditData({...editData, rating: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:border-yellow-400">
                  <option value="5">5</option><option value="4">4</option><option value="3">3</option><option value="2">2</option><option value="1">1</option>
                </select>
              </div>
              <div>
                <label className="text-sm mb-1 block">Update Avatar</label>
                <input type="file" onChange={(e) => setEditData({...editData, imageFile: e.target.files[0]})} className="w-full text-sm" />
              </div>
              <button type="submit" disabled={editLoading} className="w-full bg-[#FED547] font-bold py-2.5 rounded-lg mt-2 hover:bg-yellow-400 transition">
                {editLoading ? 'Updating...' : 'Save Changes'}
              </button>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminTestimonials;